import { useEffect, useRef } from 'react';
import { createChart } from 'lightweight-charts';
import { motion } from 'framer-motion';

const PortfolioPerformance = ({ portfolioData = [], performanceData = [], userData }) => {
  const chartContainerRef = useRef();
  const chartRef = useRef();

  useEffect(() => {
    if (!performanceData || performanceData.length === 0) {
      return;
    }

    try {
      const chartOptions = {
        layout: {
          background: { color: '#ffffff' },
          textColor: '#333',
          fontSize: 12,
        },
        grid: {
          vertLines: { visible: false },
          horzLines: { color: 'rgba(200, 200, 200, 0.2)' },
        },
        width: chartContainerRef.current.clientWidth,
        height: 200,
        timeScale: {
          timeVisible: true,
          secondsVisible: false,
          borderColor: 'rgba(200, 200, 200, 0.4)',
        },
        rightPriceScale: {
          borderColor: 'rgba(200, 200, 200, 0.4)',
        },
        crosshair: {
          vertLine: {
            color: 'rgba(79, 70, 229, 0.2)',
            width: 1,
            style: 1,
          },
          horzLine: {
            color: 'rgba(79, 70, 229, 0.2)',
            width: 1,
            style: 1,
          },
        },
      };

      chartRef.current = createChart(chartContainerRef.current, chartOptions);
      const areaSeries = chartRef.current.addAreaSeries({
        topColor: 'rgba(79, 70, 229, 0.3)',
        bottomColor: 'rgba(79, 70, 229, 0.0)',
        lineColor: 'rgba(79, 70, 229, 1)',
        lineWidth: 2,
      });

      const chartData = performanceData.map(item => ({
        time: item.month,
        value: item.value,
      }));

      areaSeries.setData(chartData);
      chartRef.current.timeScale().fitContent();

      const handleResize = () => {
        if (chartRef.current) {
          chartRef.current.applyOptions({
            width: chartContainerRef.current.clientWidth,
          });
        }
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        if (chartRef.current) {
          chartRef.current.remove();
        }
      };
    } catch (error) {
      console.error('Chart error:', error);
    }
  }, [performanceData]);

  // Calculate performance metrics based on real investment data
  const calculateMetrics = () => {
    if (!userData?.investments || userData.investments.length === 0) {
      return {
        totalReturn: 0,
        monthlyGrowth: 0,
        annualizedReturn: 0,
        activeInvestments: 0,
        totalInvestments: 0
      };
    }

    const totalInvested = userData.totalInvested || 0;
    const totalProfit = userData.totalProfit || 0;
    const totalBalance = userData.balance || 0;
    
    const totalReturn = totalInvested > 0 ? ((totalProfit / totalInvested) * 100) : 0;
    const activeInvestments = userData.investments.filter(inv => inv.status === 'active').length;
    const totalInvestments = userData.investments.length;
    
    // Calculate average monthly growth based on investment duration
    const avgInvestmentDuration = 21; // 21 days per investment
    const monthlyGrowth = totalReturn / (avgInvestmentDuration / 30);
    const annualizedReturn = monthlyGrowth * 12;

    return {
      totalReturn: totalReturn.toFixed(2),
      monthlyGrowth: monthlyGrowth.toFixed(2),
      annualizedReturn: annualizedReturn.toFixed(2),
      activeInvestments,
      totalInvestments
    };
  };

  const metrics = calculateMetrics();

  return (
    <motion.div 
      className="space-y-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* Performance Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-3 border border-emerald-200">
          <h3 className="text-xs font-medium text-emerald-700">Total Balance</h3>
          <p className="text-base font-bold mt-1 text-emerald-800">
            ${userData?.balance?.toLocaleString() || '0'}
          </p>
        </div>
        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-3 border border-indigo-200">
          <h3 className="text-xs font-medium text-indigo-700">Total Invested</h3>
          <p className="text-base font-bold mt-1 text-indigo-800">
            ${userData?.totalInvested?.toLocaleString() || '0'}
          </p>
        </div>
        <div className="bg-gradient-to-br from-rose-50 to-rose-100 rounded-lg p-3 border border-rose-200">
          <h3 className="text-xs font-medium text-rose-700">Total Profit</h3>
          <p className="text-base font-bold mt-1 text-rose-800">
            ${userData?.totalProfit?.toLocaleString() || '0'}
          </p>
        </div>
        <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-3 border border-slate-200">
          <h3 className="text-xs font-medium text-slate-700">Return Rate</h3>
          <p className={`text-base font-bold mt-1 ${
            parseFloat(metrics.totalReturn) >= 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            {metrics.totalReturn}%
          </p>
        </div>
      </div>

      {/* Performance Chart */}
      {performanceData.length > 0 ? (
        <div className="bg-white rounded-lg p-3 border border-gray-200">
          <div className="text-sm font-medium text-gray-700 mb-2">Investment Timeline</div>
          <div ref={chartContainerRef} className="w-full" />
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg p-6 text-center border border-gray-200">
          <div className="text-gray-500 text-sm">No investment data available</div>
          <div className="text-gray-400 text-xs mt-1">Start investing to see your performance chart</div>
        </div>
      )}

      {/* Portfolio Distribution */}
      <div className="grid grid-cols-2 gap-3">
        {portfolioData?.map((item, index) => (
          <div key={index} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-medium text-gray-700">{item.name}</span>
              <span className="text-xs font-bold text-gray-900">{item.value}%</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary-500 to-primary-600"
                style={{ width: `${item.value}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Investment Summary */}
      <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg p-4 border border-primary-200">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-lg font-bold text-primary-800">{metrics.activeInvestments}</div>
            <div className="text-xs text-primary-600">Active Investments</div>
          </div>
          <div>
            <div className="text-lg font-bold text-primary-800">{metrics.totalInvestments}</div>
            <div className="text-xs text-primary-600">Total Investments</div>
          </div>
          <div className="col-span-2 md:col-span-1">
            <div className="text-lg font-bold text-primary-800">{metrics.annualizedReturn}%</div>
            <div className="text-xs text-primary-600">Annualized Return</div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PortfolioPerformance; 